import { StringId, NumberId, ObjectId, FunctionId, OptionalFormatTypeId, FormatTypeId, OptionalId, typeUnionId, TypedArrayId } from "../typing/typing";
import { ProcessId } from "../framework/Processes";

/**
 * A task is an object representing a set of similar minigames with different parameters.
 *
 * A task can be:
 * - `{ name, title, generateParams, isCompleted, isAvailable, score, postprocess }` - atomic task.
 * - `{ name, components, isCompleted, isAvailable, score, onStart, postprocess }` - composite task.
 *
 * @typedef {(AtomicTask|CompositeTask)} Task
 */

export const TaskId = (obj) => typeUnionId(AtomicTaskId, CompositeTaskId, "Task")(obj);
TaskId.typeName = 'Task';

/**
 * @typedef {Object} AtomicTask
 * @property {string} name - a name of the task, whose namespace is a minigame config the task belongs to
 * @property {string} [title] - a title to show
 * @property {(form: *, move: *) => boolean} [isCompleted]  - boolean function of the form and player's move that defines when a player's move is correct and the task must be excluded from the rest of the game
 * @property {(state: *) => boolean} [isAvailable] - boolean function that prevents picking the task when `isAvailable` returns `false`
 * @property {Process} [postprocess] - a process to run after a process corresponding to a minigame terminates
 * @property {(State) => *} generateParams - parameters to add to a form of generating minigame or the whole form
 * @property {(number|(*, State) => number)} [score=1] - number of score points for completed task, or a function `(move, state) => number` for score depending on player's move and state
 * @property {(number|(State) => number)} [maxScore=1] - max available number of score points for completed task, or a function `(state) => number` for score depending on player's move and state
 * @property {boolean} [silent=false] - suppress adding a record to a history
 */

export const AtomicTaskId = FormatTypeId({
	name: StringId,
	title: OptionalId(StringId),
	data: OptionalId(ObjectId),
	isCompleted: OptionalId(FunctionId),
	isAvailable: OptionalId(FunctionId),
	postprocess: OptionalId(ProcessId),
	generateParams: OptionalId(FunctionId),
	messages: OptionalId(FunctionId),
	score: OptionalId(typeUnionId(NumberId, FunctionId, "Score")),
});
AtomicTaskId.typeName = 'Atomic task';

/**
 * @typedef {Object} CompositeTask
 * @property {string} name - a name of the task, whose namespace is a minigame config the task belongs to
 * @property {string} [title] - a title to show
 * @property {(form: *, move: *) => boolean} [isCompleted]  - boolean function of the form and player's move that defines when a player's move is correct and the task must be excluded from the rest of the game
 * @property {(state: *) => boolean} [isAvailable] - boolean function that prevents picking the task when `isAvailable` returns `false`
 * @property {Process} [postprocess] - a process to run after a process corresponding to a minigame terminates
 * @property {MinigameConfig} components - minigame config that defines a structure of the composite task
 * @property {(number, State, [*]) => number} score - number of score points awarded for the whole minigame of the composite task, depending on the sum of points of subtasks, a state and a history
 */

export const CompositeTaskId = FormatTypeId({
	name: StringId,
	title: OptionalId(StringId),
	isCompleted: OptionalId(FunctionId),
	isAvailable: OptionalId(FunctionId),
	postprocess: OptionalId(ProcessId),
	components: (obj) => MinigameConfigId(obj),
	score: OptionalId(typeUnionId(NumberId, FunctionId, "Score")),
});
CompositeTaskId.typeName = 'Composite task';

/**
 * A minigame is either an atimic task with fixed parameters or a composite task.
 * Each minigame is associated with two forms: a form representing a minigame and `GameFrame` form with the first one as subform.
 * @typedef {(CompositeTask|{name,title,form})} Minigame
 */

export const MinigameId = typeUnionId(CompositeTaskId, FormatTypeId({ name: StringId, title: StringId, form: ObjectId }));
MinigameId.typeName = 'Minigame';

/**
 * @typedef {{ minigames: [Task], constraints: [*] }} ConstrainedMinigamesConfig
 */
/**
 * A minigame config is an object that defines a structure of composite task or the whole game.
 * A minigame config can consist of arrays of tasks and constraints or an array of tasks to execute sequentially.
 * @typedef {( ConstrainedMinigamesConfig | { sequence: [Task] } )} MinigamesConfig
 */

export const ConstrainedMinigamesConfigId = FormatTypeId({ minigames: TypedArrayId(TaskId), constraints: TypedArrayId(ObjectId) });
ConstrainedMinigamesConfigId.typeName = 'Constrained minigame config';
export const MinigameConfigId = typeUnionId(ConstrainedMinigamesConfigId, FormatTypeId({ sequence: TypedArrayId(TaskId) }))
MinigameConfigId.typeName = 'Minigame config';

/**
 * @typedef {Object} HistoryRecord
 * @property {Minigame} minigame - a minigame the record is created by
 * @property {*} result - result of played minigame (e.g. a move for atomic minigame)
 * @property {boolean} completed - an indicator of whether a task of the minigame is considered to be completed (see `isCompleted`)
 * @property {boolean} finished - an indicator of whether the minigame is considered to be completed
 */